/**
 * Detects phone/tablet browsers via user agent (same rules as legacy HomeView).
 */
export function isMobileDevice(userAgent) {
  const ua = userAgent || (typeof navigator !== "undefined" && navigator.userAgent) || "";
  return (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      ua
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      ua.substr(0, 4)
    )
  );
}

/**
 * True when the browser should be treated as a phone/tablet web client.
 * Uses UA patterns, Chromium UA-CH `mobile`, iPad desktop UA, and
 * narrow + touch/coarse-pointer (covers DevTools device mode with desktop UA).
 */
export function isClientMobileWebContext() {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || "";
  // In-app / OEM browsers often missed by the legacy regex
  if (
    /SamsungBrowser|MiuiBrowser|UCBrowser|MicroMessenger|FBAN|FBAV|Instagram|Line\/|\bwv\)/i.test(
      ua
    )
  ) {
    return true;
  }
  // iOS / iPadOS tokens (covers Safari, WebKit, Chrome/Firefox/Edge on iOS)
  if (/\b(iPhone|iPad|iPod)\b/i.test(ua)) {
    return true;
  }
  if (/\b(CriOS|FxiOS|EdgiOS)\b/i.test(ua)) {
    return true;
  }
  if (isMobileDevice(ua)) {
    return true;
  }
  // Android Chrome / WebView phones often include "Mobile" in the UA
  if (/Android/i.test(ua) && /\bMobile\b/i.test(ua)) {
    return true;
  }
  // Android System WebView: "); wv)" in UA
  if (/Android/i.test(ua) && /\bwv\)/i.test(ua)) {
    return true;
  }
  // Chromium device emulation / Android Chrome: reliable when UA is still "desktop"
  try {
    const uad = navigator.userAgentData;
    if (uad && uad.mobile === true) {
      return true;
    }
  } catch (e) {
    // ignore
  }
  // iPadOS Safari often reports MacIntel + touch points
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) {
    return true;
  }
  if (!window.matchMedia) {
    return false;
  }
  const w = window.innerWidth;
  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const noHover = window.matchMedia("(hover: none)").matches;
  // Touch-first + phone/tablet width (include landscape phones, often >768 CSS px)
  if (w <= 960 && coarsePointer) {
    return true;
  }
  if (w <= 480 && noHover) {
    return true;
  }
  // Phones in landscape: wide viewport but touch device (no coarse flag in some browsers)
  if (
    w <= 960 &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 0 &&
    noHover
  ) {
    return true;
  }
  // MatchMedia on viewport: catches real phones when innerWidth/UA-CH lag on first paint
  try {
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 1024px)").matches &&
      typeof navigator.maxTouchPoints === "number" &&
      navigator.maxTouchPoints > 0 &&
      (window.matchMedia("(pointer: coarse)").matches ||
        window.matchMedia("(hover: none)").matches)
    ) {
      return true;
    }
  } catch (e) {
    // ignore
  }
  // `npm run serve` on localhost: narrow desktop window still has a desktop UA,
  // so DevTools "Responsive" alone often fails detection. Treat a phone-sized
  // window as mobile only in development (production bundles omit this branch).
  if (process.env.NODE_ENV === "development") {
    try {
      const h = window.location.hostname;
      if (
        (h === "localhost" ||
          h === "127.0.0.1" ||
          h === "[::1]") &&
        w > 0 &&
        w <= 430
      ) {
        return true;
      }
    } catch (e) {
      // ignore
    }
  }
  // Physical screen size: catches phones/tablets when UA is generic or stripped
  // (e.g. some in-app browsers, privacy tools, or stale CDN-cached HTML/JS mismatches
  // paired with real device metrics).
  try {
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-device-width: 1024px)").matches &&
      typeof navigator.maxTouchPoints === "number" &&
      navigator.maxTouchPoints > 0
    ) {
      return true;
    }
  } catch (e) {
    // ignore
  }
  return false;
}

/**
 * Which app store to promote on mobile web: "apple" | "android" | "unknown".
 * Unknown shows both badges (fallback).
 */
export function getMobileStorePlatform() {
  if (typeof navigator === "undefined") {
    return "unknown";
  }
  const ua = navigator.userAgent || "";
  if (
    /\b(iPhone|iPad|iPod)\b/i.test(ua) ||
    /\b(CriOS|FxiOS|EdgiOS)\b/i.test(ua)
  ) {
    return "apple";
  }
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) {
    return "apple";
  }
  if (/Android/i.test(ua)) {
    return "android";
  }
  return "unknown";
}

/** Query flag to preview the mobile gate on desktop (QA). */
export function isMobileAppGateForced() {
  if (typeof window === "undefined" || !window.location) {
    return false;
  }
  try {
    const q = new URLSearchParams(window.location.search);
    return q.get("mobileAppGate") === "1" || q.get("govavaMobileGate") === "1";
  } catch (e) {
    return /(?:\?|&)(mobileAppGate|govavaMobileGate)=1(?:&|$)/.test(window.location.search);
  }
}
